// src/app/inversiones/pagares/crear/crear-pagare.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router'; // <-- Importado para redirección post-guardado
import { HelpersService } from '../../../services/tools/helpers.service';
import { ClientesService } from '../../../services/clientes.service';
import { PromotoresService } from '../../../services/promotores.service';
import { InversionesService } from '../../../services/inversiones.service'; // <-- Importado

export interface DesgloseInteres {
  fechaFin: string;
  saldo: number;
  plazo: string;
  interesBruto: number;
  retencion: number;
  iva: number;
  ivaRetenido: number;
  interesNeto: number;
  acumulado: number;
  pagoInteres: number;
  pagoInteresIva: number;
  seleccionado: boolean;
}
export interface Sociedad {
  id: number;
  nombre: string;
}

@Component({
  selector: 'app-crear-pagare',
  templateUrl: './crear-pagare.component.html',
  styleUrls: ['./crear-pagare.component.scss']
})
export class CrearPagareComponent implements OnInit {

  pagareForm: FormGroup;
  desglose: DesgloseInteres[] = [];

  public promotores: any = [];
  public promotor: any = {};
  public inversionistas: any = [];
  public inversionista: any = {};
  public fechaInicio: any = null;
  public fechaFin: any = null;
  public vidaPagare: number = 0;

  // Estados de control de interfaz heredados de tu patrón profesional
  public isLoading: boolean = false;
  public errors: any = {};
  mostrarTasaRetencion: boolean = false;

  constructor(
    private fb: FormBuilder,
    private helperService: HelpersService,
    private clientesService: ClientesService,
    private promotoresServie: PromotoresService,
    private inversionesService: InversionesService, // <-- Inyectado
    private router: Router // <-- Inyectado
  ) { }

  ngOnInit() {
    this.initForm();
    this.getPromotores();
    this.getInversionistas();
    this.fechaInicio = this.obtenerFechaHoy();

    const controlRetencion = this.pagareForm.get('retencion');
    if (controlRetencion) {
      this.mostrarTasaRetencion = !!controlRetencion.value;

      controlRetencion.valueChanges.subscribe(valor => {
        this.mostrarTasaRetencion = valor;
        if (!valor) {
          const controlTasa = this.pagareForm.get('tasaRetencion');
          if (controlTasa) {
            controlTasa.setValue(0);
          }
        }
      });
    }
  }

  initForm() {
    this.pagareForm = this.fb.group({
      inversionista: ['', Validators.required],
      promotor: ['', Validators.required],
      divisa: ['MXN'],
      saldoInicial: ['', [Validators.required, Validators.min(1)]],
      sociedad: ['', Validators.required],
      fechaInicio: [new Date().toISOString().substring(0, 10), Validators.required],
      fechaFin: ['', Validators.required],
      tipo: ['Simple', Validators.required],
      frecuenciaPago: ['Mensual', Validators.required],
      tasa: ['Fija', Validators.required],
      tasaInteres: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,5})?$/)]],
      tasaRetencion: ['', [Validators.min(0)]],
      diasAnoFiscal: [360, [Validators.required]],
      retencion: [false],
      porcentajeRetencion: [''],
      diasPeriodo: ['30'],
      estrategiaPeriodicidad: ['Base'],
      excluirFechaInicio: [false],
      pagoFechaSuscripcion: [true],
      agregarInteresesPadre: [true],
    });
  }

  obtenerFechaHoy(): string {
    const hoy = new Date();
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  calcularDias() {
    if (this.fechaInicio && this.fechaFin) {
      const inicio = new Date(this.fechaInicio);
      const fin = new Date(this.fechaFin);
      const diffTime = Math.abs(fin.getTime() - inicio.getTime());
      this.vidaPagare = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
  }

  fechaInicioChanged(event: any) {
    this.fechaInicio = event;
    this.pagareForm.patchValue({ fechaInicio: event });
    this.calcularDias();
  }

  fechaFinChanged(event: any) {
    this.fechaFin = event;
    this.pagareForm.patchValue({ fechaFin: event });
    this.calcularDias();
  }

  /**
   * Petición al Backend para obtener la proyección de pagos.
   */
  calcularIntereses() {
    this.errors = {}; // Limpiar errores previos de validación

    if (!this.vidaPagare || this.vidaPagare <= 0) {
      this.helperService.notification('Atención', 'Asegúrate de ingresar un rango de fechas válido.', 'warning');
      return;
    }

    this.isLoading = true;
    this.desglose = []; // Limpiamos la tabla en pantalla

    // Payload de envío idéntico a los valores del form
    const payload = Object.assign({}, this.pagareForm.value, {
      fechaInicio: this.fechaInicio,
      fechaFin: this.fechaFin,
      vidaPagare: this.vidaPagare
    });

    this.inversionesService.calcular(payload).subscribe({
      next: (response: any) => {
        this.isLoading = false;

        // 1. Extraemos y parseamos el JSON correctamente
        const jsonResult = typeof response.json === 'function' ? response.json() : response;

        // 2. Extraemos el array 'desglose'
        if (jsonResult && jsonResult.content && jsonResult.content.desglose) {
          this.desglose = [...jsonResult.content.desglose];
        } else {
          this.desglose = [];
        }

        this.helperService.notification('Éxito', 'Cálculo de intereses proyectado.', 'success');
      },
      error: (error) => {
        this.isLoading = false;
        this.helperService.httpGlobalError(error);
      }
    });
  }

  /**
   * Envía a guardar el Pagaré principal y el desglose de pagos asociado
   */
  guardarInversion() {
    this.errors = {};

    if (this.pagareForm.invalid) {
      this.helperService.notification('Atención', 'Completa los campos requeridos antes de guardar.', 'warning');
      return;
    }

    if (!this.desglose || this.desglose.length === 0) {
      this.helperService.notification('Atención', 'Debes calcular la proyección de intereses antes de guardar.', 'warning');
      return;
    }

    this.isLoading = true;

    // Filtrar solo los periodos seleccionados por el usuario
    const desgloseAprobar = this.desglose.filter(item => item.seleccionado !== false);

    const payload = {
      ...this.pagareForm.value,
      fechaInicio: this.fechaInicio,
      fechaFin: this.fechaFin,
      vidaPagare: this.vidaPagare,
      desglose: desgloseAprobar
    };

    this.inversionesService.create(payload).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.helperService.notification('Éxito', 'Inversión y calendario registrados correctamente.', 'success');
        this.router.navigate(['/inversiones/listado']);
      },
      error: (error: any) => {
        this.isLoading = false;
        this.helperService.httpGlobalError(error);

        if (error.status === 400 || error.status === 422) {
          try {
            const errorData = typeof error.json === 'function' ? error.json() : error.error;
            if (errorData && errorData.content) {
              this.setBackendErrors(errorData.content);
            }
          } catch (e) {
            console.error('Error parseando respuesta de error:', e);
          }
        }
      }
    });
  }

  private setBackendErrors(errors: any): void {
    let temp_errors: { [key: string]: string } = {};
    Object.keys(errors).forEach((key) => {
      temp_errors[key] = errors[key][0];
    });
    this.errors = temp_errors;
  }

  // Métodos de carga de catálogos nativos se mantienen sin cambios...
  private getInversionistas() {
    this.clientesService.getAll({ order: 'nombre' }).subscribe(
      (response) => {
        const jsonResult = response.json();
        this.inversionistas = jsonResult.content;
      }, error => {
        this.helperService.httpGlobalError(error);
        if (error.status == 400) {
          this.helperService.notification('ERROR', 'No se cargaron los inversionistas.', 'error');
        }
      }
    );
  }

  private getPromotores() {
    this.promotoresServie.getAll({ order: 'nombre' }).subscribe(
      (response) => {
        const jsonResult = response.json();
        this.promotores = jsonResult.content;
      }, error => {
        this.helperService.httpGlobalError(error);
        if (error.status == 400) {
          this.helperService.notification('ERROR', 'No se cargaron los promotores.', 'error');
        }
      }
    );
  }

  public onSelectedPromotor(promotor: any) {
    this.promotor = promotor;
    this.pagareForm.patchValue({ promotor: promotor.id });
  }

  public onSelectedInversionista(inversionista: any) {
    this.inversionista = inversionista;
    this.pagareForm.patchValue({ inversionista: inversionista.id });
  }

  // Puedes mapearlo dinámicamente o definirlo estructurado
  public listaSociedadesNombres: string[] = [
    'BFV-AC', 'MYJ-AA', 'PFS-AA', 'MRK-AA', 'PFS-AC', 'BFV-AA', 'PFS-CC', 'BFV-CC', 'CVA-AA', 'KLM-AA', 'KLM-AC', 'KLM-CC'
  ];

  // Arreglo de objetos con ID para la plantilla
  public listaSociedades: Sociedad[] = this.listaSociedadesNombres.map((nombre, index) => ({
    id: index + 1, // Asigna IDs del 1 al 10
    sigla: nombre,
    nombre: nombre //
  }));

  public frecuenciasPago: string[] = [
    'Mensual', 'Bimestral', 'Trimestral', 'Cuatrimestral', 'Quinquemestral'
  ];

  limitDecimalPlaces(event: any) {
    const input = event.target;
    let value = input.value;

    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1].length > 5) {
        const truncatedValue = parts[0] + '.' + parts[1].substring(0, 5);
        input.value = truncatedValue;

        const control = this.pagareForm.get('tasaInteres');
        if (control) {
          control.setValue(Number(truncatedValue));
        }
      }
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/inversiones/pagares/crear/crear-pagare.component.ts